import {
    loadSavedEstimatesListv3,
    loadSelectedEstimate,
} from '@finpay-development/shared-platform/lib/esm/shared/state/estimator-thunk';
import {AppDispatch} from '../../shared/state/store';

export function getEstimateListId(est: any): number {
    return Number(est?.pfrEstimateId || est?.estimateId || 0);
}

export function getEstimateIocId(est: any): number {
    return Number(
        est?.estimateBody?.patientEncounterId ?? est?.patientEncounterId ?? 0
    );
}

function getEstimateCreateTime(est: any): number {
    const raw =
        est?.estimateBody?.createDt ??
        est?.createDt ??
        est?.estimateBody?.instanceCreateDt;
    return raw ? new Date(raw).getTime() : 0;
}

export function filterEstimatesByIoc(
    savedEstimations: any[] | null | undefined,
    selectedIocId: number | null | undefined
): any[] {
    if (!savedEstimations?.length) return [];
    if (!selectedIocId) return savedEstimations as any[];

    return (savedEstimations as any[]).filter(
        est => getEstimateIocId(est) === Number(selectedIocId)
    );
}

/** Patient saved-estimates list when Redux has none yet. */
export async function ensurePatientSavedEstimatesList(
    dispatch: AppDispatch,
    {
        patientId,
        savedEstimations,
        savedEstimatesLoading,
        paramId = -2,
    }: {
        patientId: number;
        savedEstimations: any[];
        savedEstimatesLoading?: boolean;
        paramId?: number;
    }
): Promise<any[]> {
    if (savedEstimations.length > 0) {
        return savedEstimations;
    }
    if (savedEstimatesLoading === true) {
        return savedEstimations;
    }

    const listResult = await dispatch(
        loadSavedEstimatesListv3({paramId, patientId})
    );
    if (
        listResult.meta.requestStatus === 'fulfilled' &&
        Array.isArray(listResult.payload)
    ) {
        return listResult.payload;
    }
    return savedEstimations;
}

function encounterIsConverted(encounter?: {
    isConverted?: boolean;
    workflow?: { workflowId?: number };
} | null): boolean {
    return !!encounter && (
        !!encounter.isConverted || encounter.workflow?.workflowId === 3
    );
}

function estimateVobId(estimate: any): number {
    return Number(estimate?.vobId ?? estimate?.estimateBody?.vobId ?? 0);
}

export function isVobLinkedToConvertedEstimate(
    vobId: number | string | null | undefined,
    patientEncounters:
        | {
              pfrEstimateId?: number;
              isConverted?: boolean;
              workflow?: { workflowId?: number };
          }[]
        | null
        | undefined,
    savedEstimations: any[] | null | undefined,
    selectedEstimate?: any | null
): boolean {
    const currentVobId = Number(vobId);
    if (!currentVobId) return false;

    const convertedEstimateIds = new Set(
        (patientEncounters ?? [])
            .filter(encounterIsConverted)
            .map((encounter) => Number(encounter.pfrEstimateId))
            .filter((id) => id > 0)
    );
    if (convertedEstimateIds.size === 0) return false;

    const estimates = [
        ...(savedEstimations ?? []),
        ...(selectedEstimate ? [selectedEstimate] : []),
    ];

    return estimates.some((estimate) => {
        const id = getEstimateListId(estimate);
        return convertedEstimateIds.has(id) && estimateVobId(estimate) === currentVobId;
    });
}

/** Prefer patientEncounters row over selectedEncounter.pfrEstimateId (can lag or be missing). */
export function getLinkedEstimateIdForIoc(
    patientEncounters:
        | {patientEncounterId?: number; pfrEstimateId?: number}[]
        | null
        | undefined,
    selectedIocId?: number | null
): number | undefined {
    if (!selectedIocId) return undefined;
    const linked = patientEncounters?.find(
        (e) => Number(e.patientEncounterId) === Number(selectedIocId)
    )?.pfrEstimateId;
    return linked != null ? Number(linked) : undefined;
}

export function estimateMatchesSelectedIoc(
    estimate: unknown,
    selectedIocId?: number | null
): boolean {
    if (!getEstimateListId(estimate)) return false;
    if (!selectedIocId) return true;
    return getEstimateIocId(estimate) === Number(selectedIocId);
}

/**
 * On VOB tab entry, keep Redux selectedEstimate when it is already a full load
 * (estimateId present) for this patient/IOC/VOB. Avoid replacing with saved-list rows.
 * IOC changes use handleHeaderIocChange and intentionally reload the estimate.
 */
export function shouldRetainSelectedEstimateOnVobTab(
    selectedEstimate: unknown,
    {
        patientId,
        selectedIocId,
        vobIdFromUrl,
    }: {
        patientId: number;
        selectedIocId?: number | null;
        vobIdFromUrl?: number | string | null;
    }
): boolean {
    if (!selectedEstimate || typeof selectedEstimate !== 'object') {
        return false;
    }
    const est = selectedEstimate as Record<string, unknown>;
    if (!getEstimateListId(est) || !est.estimateId) {
        return false;
    }

    const estPatientId =
        est.patientId ??
        (est.estimateBody as {patientId?: number} | undefined)?.patientId;
    if (estPatientId != null && Number(estPatientId) !== Number(patientId)) {
        return false;
    }

    if (selectedIocId != null && Number(selectedIocId) !== 0) {
        if (!estimateMatchesSelectedIoc(est, selectedIocId)) {
            return false;
        }
    }

    if (vobIdFromUrl != null && est.vobId != null) {
        if (Number(est.vobId) !== Number(vobIdFromUrl)) {
            return false;
        }
    }

    return true;
}

/** Load full selectedEstimate for VOB tab unless Redux already has a retained full load. */
export async function syncSelectedEstimateForVobFromListRow(
    dispatch: AppDispatch,
    getSelectedEstimate: () => unknown,
    retainContext: {
        patientId: number;
        selectedIocId?: number | null;
        vobIdFromUrl?: number | string | null;
    },
    listEstimate: unknown,
    paramId = -2
): Promise<void> {
    const currentSelectedEstimate = getSelectedEstimate();
    if (
        shouldRetainSelectedEstimateOnVobTab(
            currentSelectedEstimate,
            retainContext
        )
    ) {
        return;
    }

    const targetId = getEstimateListId(listEstimate);
    if (!targetId) {
        return;
    }

    await dispatch(
        loadSelectedEstimate({
            paramId,
            estimationId: String(targetId),
        })
    );
}

/** Prefer selectedEstimate when it matches header IOC; otherwise pick from list. */
export function resolveEstimateForHeaderIoc(
    savedEstimations: any[] | null | undefined,
    selectedIocId: number | null | undefined,
    linkedEstimateId: number | null | undefined,
    selectedEstimate?: any | null
): any | undefined {
    if (
        selectedEstimate &&
        estimateMatchesSelectedIoc(selectedEstimate, selectedIocId)
    ) {
        return selectedEstimate;
    }
    return pickEstimateForIoc(
        savedEstimations,
        selectedIocId,
        linkedEstimateId
    );
}

// Prefer IOC-tethered (pfrEstimateId), otherwise latest by createDt
export function pickEstimateForIoc(
    savedEstimations: any[] | null | undefined,
    selectedIocId?: number | null,
    linkedEstimateId?: number | null
): any | undefined {
    const filtered = filterEstimatesByIoc(savedEstimations, selectedIocId);
    if (!filtered.length) return undefined;

    if (linkedEstimateId) {
        const tethered = filtered.find(
            est => getEstimateListId(est) === Number(linkedEstimateId)
        );
        if (tethered) return tethered;
    }

    return [...filtered].sort((a, b) => {
        const byDate = getEstimateCreateTime(b) - getEstimateCreateTime(a);
        if (byDate !== 0) return byDate;
        return getEstimateListId(b) - getEstimateListId(a);
    })[0];
}
